<template>
  <div class="mt-2 px-6 federal-flsa-survey-list-container">
    <LibrariesSkeletonLoader
      class="ml-6 mr-6"
      :is-loading="libraryLoading"
      is-requisition
    />

    <div v-if="!libraryLoading">
      <NoDataBlock
        v-if="showNoDataBlock"
        label="Federal FLSA"
        document-type="Report"
        :show-image="false"
      >
        <template #description>
          There is no job profile data available for your selection.
        </template>
      </NoDataBlock>
      <template v-else>
        <FederalFlsaDraftSurveyList v-if="viewMode === 'draft'" />
        <FederalFlsaHrisSurveyList v-else />
      </template>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import LibrariesSkeletonLoader
  from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import { useAppLoading } from '@/stores/app/loading';
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useFlsaSurveysStore } from '@/stores/jobProfiles/flsaSurveys';
import NoDataBlock from '@/components/library/NoDataBlock.vue';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useFilters } from '@/stores/filters/filters';
import { useAppBase } from '@/stores/app/base';
import { useRoute, useRouter } from 'vue-router';
import { tooltipKeys } from '@/helpers/constants/tooltipKeys';
import { useTooltips } from '@/stores/insights/tooltips';
import FederalFlsaDraftSurveyList from '@/components/library/flsaSurveys/FederalFlsaDraftSurveyList.vue';
import FederalFlsaHrisSurveyList from '@/components/library/flsaSurveys/FederalFlsaHrisSurveyList.vue';

const emit = defineEmits(['track-page-load']);

const route = useRoute();
const router = useRouter();

const { pageLoading, listLoading } = storeToRefs(useAppLoading());
const { areFiltersApplied } = storeToRefs(useFilters());
const { profiles, textFilter, viewMode } = storeToRefs(useFlsaSurveysStore());

const {
  setTextFilter,
  loadPage: loadFlsaSurveysPage,
  resetCountryDefault,
  resetSelectedAlertStatus,
  setViewMode: setFlsaViewMode,
} = useFlsaSurveysStore();

const { setFilterConfig } = useFilters();
const { showFilterBarSettings } = useAppBase();

const libraryLoading = computed(() => pageLoading.value || listLoading.value);

const hasFiltersOrSearch = computed(() => {
  const hasSearch = Boolean(route.query?.q);

  return hasSearch || areFiltersApplied.value;
});

const showNoDataBlock = computed(() => (
  !libraryLoading.value
  && profiles.value.length === 0
  && !hasFiltersOrSearch.value
));

const loadPage = (params) => {
  const searchTerm = textFilter.value || params.searchTerm;

  const routeQuery = params.routeQuery || {
    ...params.selectedFilters,
    ...(params.page && { page: params.page }),
    ...(params.selectedMyJobs && { owned: params.selectedMyJobs }),
    ...(params.searchTerm && { q: params.searchTerm }),
    ...(params.selectedSubMenuOption && { sort: params.selectedSubMenuOption }),
  };

  loadFlsaSurveysPage({
    ...params,
    routeQuery,
    searchTerm,
  });
};

onMounted(async () => {
  const urlView = route.query?.view;

  if (urlView === 'draft') setFlsaViewMode('draft');
  else if (urlView === 'hris') setFlsaViewMode('hris_linked');

  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'flsaSurveys',
    endpoint: 'flsaSurveysLibrary',
    datepicker: false,
    ignorableFilters: ['alert_type', 'jurisdiction', 'view'],
  });

  showFilterBarSettings({ route: route.name });

  const searchTerm = route.query?.q;

  if (searchTerm) setTextFilter(searchTerm);

  await useTooltips().loadTooltips(tooltipKeys['federal-flsa-dashboard'] || []);

  loadPage({
    routeQuery: route.query || '',
    router,
    page: route.query?.page || 1,
  });

  emit('track-page-load', { page: 'Federal FLSA Dashboard: Open' });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);
  setTextFilter('');
  resetCountryDefault();
  resetSelectedAlertStatus(router);
  useFlsaSurveysStore().reset();

  setFilterConfig({
    subMenuType: null,
    ignorableFilters: [],
  });
});
</script>
